import { Injectable } from '@angular/core';
import { AppState } from '@app/app-state';
import { LocalStorage } from '@features/storage/local-storage';
import { isPresent } from '@imcc/utilities';
import { Store } from '@ngrx/store';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable } from 'rxjs';
import {
  filter, first, map, startWith,
} from 'rxjs/operators';
import { ICognitoIdToken } from './i-cognito-id-token';
import { UserActions } from './user-actions.enum';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private oauthService: OAuthService,
    private store: Store<AppState>,
    private localStorage: LocalStorage,
  ) {}

  public state$ = this.oauthService.events.pipe(
    startWith(this.stateSnapshot),
    map(() => this.stateSnapshot),
  );

  public identity$: Observable<ICognitoIdToken> = this.state$.pipe(
    map(() => this.oauthService.getIdentityClaims() as ICognitoIdToken),
    filter<ICognitoIdToken>(Boolean),
  );

  public isAuthenticated$ = this.state$.pipe(
    map(() => this.isAuthenticated()),
  );

  public becameAuthenticated$ = this.isAuthenticated$.pipe(
    filter<boolean>(Boolean),
    first(),
  );

  public isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  public logout(): void {
    this.store.dispatch({ type: UserActions.LOGOUT });
    this.oauthService.logOut();
    this.localStorage.clear();
  }

  public async refreshToken(): Promise<void> {
    if (isPresent(this.oauthService.getRefreshToken())) {
      await this.oauthService.refreshToken();
    }
  }

  public async configure(): Promise<boolean> {
    return this.oauthService
      .loadDiscoveryDocumentAndLogin()
      .catch(() => false)
      .then(() => {
        this.oauthService.setupAutomaticSilentRefresh();
        return true;
      })
      .catch(() => false);
  }

  private get stateSnapshot() {
    return {
      idToken: this.oauthService.getIdToken(),
      accessToken: this.oauthService.getAccessToken(),
      refreshToken: this.oauthService.getRefreshToken(),
    };
  }
}
